import { useEffect, useRef, useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Search01Icon, Cancel01Icon, UserIcon } from '@hugeicons/core-free-icons';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

// initialsOf deriva las iniciales (primera y última palabra) de un nombre.
function initialsOf(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * UserPicker — Autocomplete that searches users by name or email.
 * Supports free-text entry when allowFreeText is true.
 *
 * @param {object} props
 * @param {string} props.value - Current display value (name or email text)
 * @param {(user: {id, name, email, title, department}) => void} props.onSelect - Callback when a user is selected
 * @param {() => void} [props.onClear] - Callback when the selection is cleared
 * @param {string} [props.placeholder] - Input placeholder
 * @param {string} [props.testId] - data-testid for testing
 * @param {boolean} [props.allowFreeText] - Allow typing a name manually without selecting from the list
 * @param {(text: string) => void} [props.onFreeText] - Callback when free text is confirmed
 */
export function UserPicker({
  value,
  onSelect,
  onClear,
  placeholder = 'Buscar por nombre o correo...',
  testId,
  allowFreeText = false,
  onFreeText,
  autoOpen = false,
}) {
  const [open, setOpen] = useState(autoOpen);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        // Busca primero en el directorio de la organización (EntraID vía Graph).
        // Si el directorio no está disponible, cae a la búsqueda local de usuarios
        // ya registrados en VIA, para no dejar al usuario sin resultados.
        let people = [];
        try {
          const { data } = await api.get('/users/directory-search', { params: { q: query } });
          people = (data || []).map((p) => ({
            id: p.oid || p.id || '',
            oid: p.oid || '',
            name: p.name,
            email: p.email,
            title: p.title || '',
            department: p.department || '',
            initials: initialsOf(p.name),
            fromDirectory: true,
          }));
        } catch {
          people = [];
        }

        if (people.length === 0) {
          const { data } = await api.get('/users/search', { params: { q: query } });
          people = data || [];
        }

        setResults(people);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const handleSelect = (user) => {
    onSelect(user);
    setOpen(false);
    setQuery('');
  };

  const handleClear = () => {
    if (onClear) onClear();
    setQuery('');
    setResults([]);
  };

  // If we have a selected value, show it as a chip-like display
  if (value) {
    return (
      <div
        className="flex items-center gap-2 h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
        data-testid={testId}>
        <HugeiconsIcon icon={UserIcon} className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="truncate flex-1">{value}</span>
        <button
          type="button"
          onClick={handleClear}
          className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Limpiar selección">
          <HugeiconsIcon icon={Cancel01Icon} className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={placeholder}
          data-testid={testId}
          className={cn(
            'flex items-center gap-2 h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm',
            'text-muted-foreground hover:border-ring transition-colors cursor-pointer',
          )}>
          <HugeiconsIcon icon={Search01Icon} className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{placeholder}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <HugeiconsIcon icon={Search01Icon} className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
              placeholder={placeholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && allowFreeText && query.length >= 2 && results.length === 0) {
                  e.preventDefault();
                  if (onFreeText) onFreeText(query);
                  setOpen(false);
                  setQuery('');
                }
              }}
              aria-label="Buscar usuario"
              autoFocus
            />
          </div>
          <CommandList>
            {loading && <div className="py-4 text-center text-xs text-muted-foreground">Buscando...</div>}
            {!loading && query.length >= 2 && results.length === 0 && (
              <CommandEmpty>
                {allowFreeText ? (
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted rounded-sm cursor-pointer"
                    onClick={() => {
                      if (onFreeText) onFreeText(query);
                      setOpen(false);
                      setQuery('');
                    }}>
                    Usar "<span className="font-medium">{query}</span>"
                  </button>
                ) : (
                  'No se encontraron usuarios'
                )}
              </CommandEmpty>
            )}
            {results.length > 0 && (
              <CommandGroup>
                {results.map((user) => (
                  <CommandItem
                    key={user.id}
                    value={user.id}
                    onSelect={() => handleSelect(user)}
                    className="cursor-pointer group/item">
                    <div className="flex items-center gap-3 w-full">
                      <div className="flex items-center justify-center h-7 w-7 rounded-full bg-primary/10 text-primary group-data-[selected=true]/item:bg-accent-foreground/15 group-data-[selected=true]/item:text-accent-foreground text-[10px] font-semibold shrink-0">
                        {user.initials || user.name?.slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{user.name}</p>
                        <p className="text-[11px] text-muted-foreground group-data-[selected=true]/item:text-accent-foreground/70 truncate">
                          {user.email}
                          {user.title && ` · ${user.title}`}
                        </p>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
